import type { Coordinate } from "./models"

/**
 * NPC CONVERSATIONS: a tree an NPC talks through with one player at a time, authored as data: Ember's trees
 * are the catalog input `emberConversations` (content/game/conversations/ember.json), named `ember/<name>`. The server walks it (campaigns/atoms/conversations.ts)
 * and sends the player one node at a time; the client shows it with the rest of the HUD out of the way
 * (gui/conversation_panel.ts).
 *
 * A node is what the NPC says (one or more lines, shown one after another) and what the player can
 * answer. Start nodes and answers carry conditions on the player's state; nodes and answers carry
 * effects, applied by the server when the node is reached or the answer is picked.
 */

/** Something about the player that must hold. Every field given must hold. */
export interface ConversationCondition {
    /** A player flag that must be set (truthy), or, with `is`, hold exactly that value. */
    flag?: string
    /** The value `flag` must hold (`{ "flag": "emberChapter", "is": 1 }`). */
    is?: string | number | boolean
    /** A player flag that must not be set. */
    notFlag?: string
    /** Items the player must carry. */
    has?: { item: string; quantity?: number }
    /** Items the player must not carry (fewer than `quantity`, default 1). */
    lacks?: { item: string; quantity?: number }
}

/** What happens. Exactly one field per effect. */
export interface ConversationEffect {
    /** Set a player flag (to true). */
    set?: string
    /** Clear a player flag. */
    clear?: string
    /** Hand the player items. */
    give?: { item: string; quantity?: number }
    /** Take items from the player; the answer is refused if they no longer carry them. */
    take?: { item: string; quantity?: number }
    /** A named signal for the game's services (conversation_signals.ts), with the NPC and the player. */
    emit?: string
    /** Make every `once` answer in this tree available to the player again. */
    resetOnce?: boolean
}

export interface ConversationChoice {
    id: string
    /** What the player says. */
    say: string
    /** Only offered (and only taken) while this holds; an array must all hold. */
    when?: ConversationCondition | ConversationCondition[]
    do?: ConversationEffect[]
    /** The node it leads to; none, or "end", ends the conversation. */
    go?: string
    /** Offered until taken once, ever, by this player. */
    once?: boolean
}

export interface ConversationNode {
    /** What the NPC says, a line at a time. */
    lines: string[]
    /** Applied when the node is reached. */
    do?: ConversationEffect[]
    choices?: ConversationChoice[]
    /** Where a node without choices goes once its lines are read; none, or "end", ends it. */
    next?: string
}

export interface ConversationTree {
    id: string
    /** The name shown over the NPC's lines. */
    speaker: string
    /** The first start whose condition holds opens the conversation. */
    start: { node: string; when?: ConversationCondition | ConversationCondition[] }[]
    nodes: Record<string, ConversationNode>
}

/** One node as the client is sent it: the answers already filtered for the player. */
export interface ConversationView {
    npcEntityId: string
    speaker: string
    nodeId: string
    lines: string[]
    choices: { id: string; say: string }[]
    /** Counts every node sent in this conversation, so the client can tell a new node from a resend. */
    step: number
}

/** The answer a node without choices takes when its last line is read. */
export const CONTINUE_CHOICE = "__continue"

/**
 * How near the NPC a player must stand, as the gap between the two bodies (their centres, less a body's
 * width): within START to begin a conversation, beyond LEAVE and it ends. Start is the tighter of the two,
 * so a player who began at the edge doesn't end it with a twitch. A tile, as the design asked.
 */
export const CONVERSATION_BODY_RADIUS = 12
export const CONVERSATION_START_GAP = 24
export const CONVERSATION_LEAVE_GAP = 32

export const conversationGap = (a: Coordinate, b: Coordinate): number =>
    Math.hypot(a.x - b.x, a.y - b.y) - 2 * CONVERSATION_BODY_RADIUS

/** The flag that marks a `once` answer taken. */
export const onceFlag = (treeId: string, nodeId: string, choiceId: string): string => `${onceFlagPrefix(treeId)}${nodeId}:${choiceId}`

/** What every `once` flag of a tree starts with. */
export const onceFlagPrefix = (treeId: string): string => `talked:${treeId}:`

/**
 * What is wrong with a tree: no start, missing or empty nodes, dangling links, answers without ids, and (given
 * `isItem`) items the content does not have. Checked when content compiles and when a tree is read live.
 */
export const conversationTreeProblems = (tree: unknown, isItem?: (id: string) => boolean): string[] => {
    const t = tree as ConversationTree
    if (!t || typeof t !== "object" || !t.nodes || typeof t.nodes !== "object" || !Array.isArray(t.start) || !t.start.length) {
        return ["not a tree: it needs start nodes and nodes"]
    }
    const problems: string[] = []
    const known = (node: string | undefined) => !node || node === "end" || !!t.nodes[node]
    const items = (at: string, list: (ConversationCondition | ConversationEffect)[] | undefined) => {
        if (!isItem) return
        for (const entry of list ?? []) {
            for (const ref of [(entry as any).has, (entry as any).lacks, (entry as any).give, (entry as any).take]) {
                if (ref && !isItem(ref.item)) problems.push(`${at} names unknown item "${ref.item}"`)
            }
        }
    }
    const conditions = (when: ConversationCondition | ConversationCondition[] | undefined) => when === undefined ? [] : Array.isArray(when) ? when : [when]
    t.start.forEach((start, i) => {
        if (!t.nodes[start?.node]) problems.push(`start ${i} names missing node "${start?.node}"`)
        items(`start ${i}`, conditions(start?.when))
    })
    for (const [id, node] of Object.entries(t.nodes)) {
        if (!Array.isArray(node?.lines) || !node.lines.length || node.lines.some(line => typeof line !== "string" || !line)) {
            problems.push(`node "${id}" needs lines`)
        }
        if (!known(node?.next)) problems.push(`node "${id}" goes to missing "${node.next}"`)
        items(`node "${id}"`, node?.do)
        const seen = new Set<string>()
        node?.choices?.forEach(choice => {
            if (!choice?.id || !choice?.say) problems.push(`node "${id}" has an answer without id or say`)
            else if (seen.has(choice.id)) problems.push(`node "${id}" has two answers "${choice.id}"`)
            seen.add(choice?.id)
            if (!known(choice?.go)) problems.push(`answer "${id}/${choice?.id}" goes to missing "${choice?.go}"`)
            items(`answer "${id}/${choice?.id}"`, [...conditions(choice?.when), ...(choice?.do ?? [])])
        })
    }
    return problems
}
